import { CdkDragEnd, CdkDragStart, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { IMark, IMarkItems, MarkPosition } from '@atlas-workspace/shared/models';
import { PlanMarkService } from '@atlas-workspace/shared/service';
import { of } from 'rxjs';
import { delay, take } from 'rxjs/operators';

@Component({
  selector: 'atl-image-mark',
  templateUrl: './image-mark.component.html',
  styleUrls: ['./image-mark.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, DragDropModule],
})
export class ImageMarkComponent implements AfterViewInit {
  @Input() set mark(item: IMark | undefined) {
    if (!item || !item.mark) {
      this.markItems = [];
      this._marks = item;
      return;
    }

    // Check if we're just adding a new pin
    const newMarks = item.mark;
    const currentMarks = this._marks?.mark || [];

    if (newMarks.length === currentMarks.length + 1) {
      // We're adding exactly one new pin - find which one is new
      const newMark = newMarks.find(
        (mark) =>
          !currentMarks.some(
            (existing) =>
              existing.x === mark.x &&
              existing.y === mark.y &&
              (existing.order === mark.order || (!existing.order && !mark.order)),
          ),
      );

      if (newMark && this.width && this.height) {
        // Calculate safe order that doesn't conflict with existing orders
        const existingOrders = new Set(this.markItems.map(markItem => markItem.order));
        let order = newMark.order;

        if (!order) {
          // Find the highest order and add 1, or use newMarks.length if no conflicts
          const maxExistingOrder = Math.max(0, ...Array.from(existingOrders));
          order = Math.max(newMarks.length, maxExistingOrder + 1);
        }

        const left = newMark.x + '%';
        const markBlockValue = this.markBlock || (32 * 100) / this.height;
        const top = newMark.y - markBlockValue + '%';
        const width = 32;
        const height = 32;
        const x = newMark.x;
        const y = newMark.y;

        this.markItems.push({ order, top, left, width, height, x, y });
        this._marks = item;
        this.cdr.detectChanges();
        return;
      }
    }

    // Check if we're removing a pin
    if (newMarks.length === currentMarks.length - 1 && currentMarks.length > 0) {
      const removedMark = currentMarks.find(
        (existing) =>
          !newMarks.some(
            (mark) =>
              mark.x === existing.x &&
              mark.y === existing.y &&
              ((mark.order && existing.order && mark.order === existing.order) || (!mark.order && !existing.order)),
          ),
      );

      if (removedMark) {
        // Find the corresponding visual markItem by coordinates instead of order
        const markItemToRemove = this.markItems.find(markItem => {
          const coordDiffX = Math.abs(markItem.x - removedMark.x);
          const coordDiffY = Math.abs(markItem.y - removedMark.y);
          return coordDiffX < 0.01 && coordDiffY < 0.01;
        });

        if (markItemToRemove) {
          this.markItems = this.markItems.filter((markItem) => markItem !== markItemToRemove);
          this._marks = item;
          this.cdr.detectChanges();
          return;
        }
      }
    }

    // Check if this is a drag update cycle (same number of pins, but coordinates changed)
    if (newMarks.length === currentMarks.length && currentMarks.length > 0) {
      let isDragUpdate = false;
      for (let i = 0; i < newMarks.length; i++) {
        const newMark = newMarks[i];
        const currentMark = currentMarks[i];
        if (
          newMark &&
          currentMark &&
          (Math.abs(newMark.x - currentMark.x) > 0.1 || Math.abs(newMark.y - currentMark.y) > 0.1) &&
          (newMark.order === currentMark.order || (!newMark.order && !currentMark.order))
        ) {
          isDragUpdate = true;
          break;
        }
      }

      if (isDragUpdate) {
        // This is a drag update from server - preserve visual positions
        this._marks = item;
        this.markItems.forEach((markItem) => {
          const correspondingMark = newMarks.find((mark) => {
            const markOrder = mark.order || newMarks.indexOf(mark) + 1;
            return markOrder === markItem.order;
          });
          if (correspondingMark) {
            markItem.x = correspondingMark.x;
            markItem.y = correspondingMark.y;
          }
        });
        this.cdr.detectChanges();
        return;
      }

      if (this.markItems.length > 0) {
        let isDataRefresh = true;
        const coordinateMatches = new Map<string, boolean>();

        this.markItems.forEach((existingItem) => {
          const matchingNewMark = newMarks.find((newMark) => {
            const coordDiffX = Math.abs(newMark.x - existingItem.x);
            const coordDiffY = Math.abs(newMark.y - existingItem.y);
            return coordDiffX < 0.01 && coordDiffY < 0.01;
          });

          if (matchingNewMark) {
            const key = `${existingItem.x.toFixed(2)}-${existingItem.y.toFixed(2)}`;
            coordinateMatches.set(key, true);
          } else {
            isDataRefresh = false;
          }
        });

        if (isDataRefresh && coordinateMatches.size === this.markItems.length) {
          this._marks = item;
          this.markItems.forEach((markItem) => {
            const correspondingMark = newMarks.find((mark) => {
              const coordDiffX = Math.abs(mark.x - markItem.x);
              const coordDiffY = Math.abs(mark.y - markItem.y);
              return coordDiffX < 0.01 && coordDiffY < 0.01;
            });
            if (correspondingMark) {
              markItem.x = correspondingMark.x;
              markItem.y = correspondingMark.y;
              if (correspondingMark.order) {
                markItem.order = correspondingMark.order;
              }
            }
          });
          this.cdr.detectChanges();
          return;
        }
      }
    }

    // Full redraw needed (for initial load, complete replacement, etc.)
    const existingPositions = new Map<number, { left: string; top: string }>();
    this.markItems.forEach((markItem) => {
      existingPositions.set(markItem.order, {
        left: markItem.left,
        top: markItem.top,
      });
    });

    this.markItems = [];
    this._marks = item;
    this.markListen(existingPositions);
  }
  @Input() editor = false;
  @Input() imageUrl?: string;
  @Input() floorId!: number;
  @Input() floorNumber!: number;
  @Input() floorType!: string;
  @Input() multiplePins = false;
  @Input() disableDrag = true;
  @Input() lastOrder?: number;
  @Output() readonly setMark = new EventEmitter<IMark>();
  @Output() private readonly existingMarkClick = new EventEmitter<IMarkItems>();
  @Output() readonly imageLoad = new EventEmitter();

  public markItems: IMarkItems[] = [];
  public width!: number;
  public height!: number;
  private heightContainer?: number;
  private markBlock!: number;
  private _marks?: IMark;
  public isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private offsetX = 0;
  private offsetY = 0;

  private currentMark!: IMarkItems;
  private readonly respositionDelay = 150;
  private readonly firstLoadDelay = 500;

  @ViewChild('imageWrap') imageWrapperRef!: ElementRef;
  @ViewChild('image') imgRef!: ElementRef;

  @HostListener('window:orientationchange')
  onOrientationChange(): void {
    of(true)
      .pipe(take(1), delay(this.respositionDelay))
      .subscribe(() => {
        this.initImage(true);
      });
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly planMarkService: PlanMarkService,
  ) {}

  ngAfterViewInit(): void {
    if (this.imageWrapperRef) {
      of(true)
        .pipe(take(1), delay(this.firstLoadDelay))
        .subscribe(() => {
          this.initImage();
        });
    }
  }

  public markTrackBy(index: number, item: IMarkItems): number {
    return item.order;
  }

  public clickOnExistingMark(v: IMarkItems, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.isDragging || !this.multiplePins) {
      return;
    }

    this.existingMarkClick.emit(v);
  }

  public onMark($event: MouseEvent): void {
    if (this.width && this.height && !this.isDragging) {
      const x = Math.round(($event.offsetX * 100) / this.width);
      const y = Math.round(($event.offsetY * 100) / this.height);
      this.setMark.emit({
        mark: [{ x, y, ...(typeof this.lastOrder === 'number' ? { order: this.lastOrder + 1 } : {}) }],
        floorId: this.floorId,
        floorNumber: this.floorNumber,
        floorType: this.floorType,
      });
    }
  }

  public onMarkerDragStart(event: CdkDragStart, marker: IMarkItems): void {
    if (this.disableDrag) {
      return;
    }
    this.isDragging = true;
    this.currentMark = marker;
    this.dragStartX = event.source.getFreeDragPosition().x;
    this.dragStartY = event.source.getFreeDragPosition().y;
    this.offsetX = parseFloat(this.currentMark.left) - this.dragStartX;
    this.offsetY = parseFloat(this.currentMark.top) - this.dragStartY;
    if (this._marks && this._marks.mark && this._marks.mark[this.currentMark.order - 1]) {
      this._marks.mark[this.currentMark.order - 1].dragOriginX = marker.x;
      this._marks.mark[this.currentMark.order - 1].dragOriginY = marker.y;
    }
  }

  private initImage(reloadImg: boolean = false): void {
    const el: HTMLDivElement = this.imageWrapperRef.nativeElement;
    this.heightContainer = el.clientHeight;
    if (reloadImg) {
      this.loadImage(this.imgRef.nativeElement);
    }
  }

  public onDragEnd(e: CdkDragEnd, mark: any): void {
    if (this.disableDrag) {
      return;
    }
    of(null)
      .pipe(delay(100))
      .subscribe(() => {
        this.isDragging = false;
        if (this.currentMark && this._marks && this.width && this.height) {
          const dropPoint = e.source.getFreeDragPosition();
          const originX = mark.dragOriginX !== undefined ? mark.dragOriginX : mark.x;
          const originY = mark.dragOriginY !== undefined ? mark.dragOriginY : mark.y;
          const originalLeftPx = (originX / 100) * this.width;
          const originalTopPx = (originY / 100) * this.height;
          const newLeftPx = originalLeftPx + dropPoint.x;
          const newTopPx = originalTopPx + dropPoint.y;
          const newX = (newLeftPx / this.width) * 100;
          const newY = (newTopPx / this.height) * 100;
          const newMark: MarkPosition = {
            x: newX,
            y: newY,
            position: this.currentMark.order - 1,
          };
          this.planMarkService.setMarkPosition(newMark);

          delete mark.dragOriginX;
          delete mark.dragOriginY;
        }
      });
  }

  public loadImage(image: HTMLImageElement): void {
    this.imageLoad.emit();
    if (this.heightContainer) {
      const cof = this.heightContainer / +image.naturalHeight;
      this.height = this.heightContainer;
      this.width = Math.floor(+image.naturalWidth * cof);
      this.markBlock = (32 * 100) / this.height;
      this.markListen();
    } else {
      this.initImage(true);
    }
  }

  private markListen(existingPositions?: Map<number, { left: string; top: string }>): void {
    if (!this._marks) return;
    this.markItems = [];
    this._marks?.mark?.forEach((mark, i: number) => {
      const order = mark.order || i + 1;
      if (this.width && this.height) {
        const existingPosition = existingPositions?.get(order);

        const left = existingPosition?.left || mark.x + '%';
        const top = existingPosition?.top || mark.y - this.markBlock + '%';

        const width = 32;
        const height = 32;
        const x = mark.x;
        const y = mark.y;
        this.markItems.push({ order, top, left, width, height, x, y });
        this.cdr.detectChanges();
      }
    });
  }
}
